import React, { createRef, Component } from 'react';
import PropTypes from 'prop-types';
import {reaction, when, values} from 'mobx';
import {observer} from 'mobx-react';
import $ from 'jquery';
import classNames from "classnames";

import ElementSpacer from "../utilities/ElementSpacer";
import Filters from "./geo/Filters";
import InteractiveCollectionMap from '../map/InteractiveCollectionMap';

import Block from "../blocks/SchoolBlocks/_Block";
import {StoreContext} from "../../stores/StoreLoader";
import {gridTypesEnum, renderGoogleLanguage} from "../../utils/SchoolBlocksUtilities";
import {normalizeStringWithoutDiacritics} from "../../utils/InternationalStringUtilities";
import {bindSchoolBlocksDraggabillyEvents, sortItems} from "./PackeryMods";
import { sb_updateGrid, getBlockPosition } from "../blocks/SchoolBlocks/blockUtils";
import {ErrorBoundary} from "../utilities/ErrorBoundary";
import styles from "./styles/PackeryGrid.module.scss";
import {isJson} from "../admin/spinup/utilities";

let totalAsyncBlocksLoaded = 0;

@observer
class PackeryGrid extends Component {
    static contextType = StoreContext;

    static propTypes = {
        htmlId: PropTypes.string.isRequired,
        rowHeightClass: PropTypes.string.isRequired,
        blocks: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
        categoryTree: PropTypes.arrayOf(PropTypes.object),
        blockTypes: PropTypes.object,
        textFilterPlaceholder: PropTypes.string,
        gridType: PropTypes.string.isRequired,
        numAsyncBlocks: PropTypes.number,
        hasMicrosite: PropTypes.bool,
        isPersonGrid: PropTypes.bool,
    };

    static defaultProps = {
        rowHeightClass: 'sb-row-height',
        blocks: [],
        textFilterPlaceholder: "",
        numAsyncBlocks: 0,
    };

    constructor(props, context) {
        super(props);

        this.gridRef = createRef();

        this.gridSelector = '#' + props.htmlId;
        this.packeryLayoutTimer = null;
        this.packeryReloadTimer = null;

        this.state = {
            packeryInstance: null,
        }

        this.buildGrid(context);
        this.handleFinalAsyncBlockLoaded = this.handleFinalAsyncBlockLoaded.bind(this);
    }

    componentDidMount() {
        const {gridStore} = this.context;

        this.gridElement = this.gridRef.current;

        this.tickTotalAsyncBlocksLoaded = this.handleFinalAsyncBlockLoaded();

        when(() => gridStore.blocks.size > 0, this.initializePackery);
    }

    getMaxColumns() {
        return this.state.packeryInstance?.options?.maxColumns || 4;
    }

    setMaxColumns(cols) {
        if (this.state.packeryInstance) {
            this.state.packeryInstance.options.maxColumns = cols;
        }
    }

    initializePackery = () => {
        totalAsyncBlocksLoaded = 0;
        const {gridStore, interfaceStore} = this.context;
        // binds $().packery to global Packery object so that prototypes (PackeryMods) work
        // this is a bridge to get vanilla Packery working properly with jQuery plugin Packery and can be removed once we don't have jQuery/global JS
        const Packery = require('packery');
        Packery.prototype.bindSchoolBlocksDraggabillyEvents = bindSchoolBlocksDraggabillyEvents;
        Packery.prototype.sortItems = sortItems;

        var jQBridget = require('jquery-bridget');
        $.bridget('packery', Packery, $);

        // Initialize this using jQuery for now.  TODO: move this to vanilla JS.
        // Before we move fully away from jQuery and stuff, make sure we can still
        // use the PackeryMods.js code!!
        const imagesLoaded = require("imagesloaded");
        const $mainGrid = $(this.gridElement);
        $mainGrid.packery({
            'columnWidth': '.sb-column-width',
            'rowHeight': '.' + this.props.rowHeightClass,
            'itemSelector': '.sb-block',
            'isResizeBound': false, // don't let Packery bind to the window resize event, let's manually do this in reloadItems()
            'maxColumns': this.getMaxColumns(),
        });

        React.startTransition(() => {
            this.setState({
                packeryInstance: $mainGrid.data('packery'),
            }, () => {
                this.state.packeryInstance.on('layoutComplete', this.layoutComplete);
                this.state.packeryInstance.on('dragItemPositioned', this.onBlockDrag);

                // in cases where there are no blocks on page load so packery is not immediately initialized, and then blocks
                // are added, this is important so those items are loaded when packery is initialized
                this.reloadItems();
            })
        })

        imagesLoaded(this.gridElement)
            .on('progress', this.packeryLayout)
            .on('always', this.packeryLayout);

        // we need to track block visibility and relayout the entire grid when it changes (since one block's
        // visibility may effect the entire grid).
        reaction(() => [gridStore.visibleBlockIds, interfaceStore.width], this.packeryLayout);
        // Reload items changes when our loadedBlockIds change.  This happens when buildGrid() is called *and*
        // when the FluxCapacitor timeline changes things in the gridStore.
        reaction(() => gridStore.loadedBlockIds, this.reloadItems);
        // we're switching grid sizes here, so we need to reload
        reaction(() => this.props.hasMicrosite, this.reloadItems);
    }

    buildGrid = context => {
        const {gridStore, organizationStore} = context;
        // turn these JSON blocks to instance of BlockObject

        gridStore.setId(organizationStore.currentOrganization.id);
        gridStore.setGridType(this.props.gridType);

        if (this.props.gridType === gridTypesEnum.ALPHA_GRID) {
            // We only sort the blocks alphabetically when it's an ALPHA_GRID, otherwise we use the block insertion order
            gridStore.setMapEnabled(false);
        } else {
            gridStore.setMapEnabled(this.props.displayMap);
        }

        gridStore.categoryFilters.setFilters(this.props.categoryTree || [], true, true);
        gridStore.tagFilters.setFilters(this.props.tags || [], true);
        gridStore.blockTypeFilters.setFilters(this.props.blockTypeFilters || [], true, true);
    };

    layoutComplete = () => {
        // TODO: trigger an event so that TrackVisiblity knows that things have moved around.
        if (window && window.scrollX !== undefined && window.scrollY !== undefined) {
            window.scrollTo(window.scrollX, window.scrollY - 1);
            window.scrollTo(window.scrollX, window.scrollY + 1);
        }
    };

    packeryLayout = () => {
        if (this.state.packeryInstance) {
            // timeout is important for block filtering to work
            clearTimeout(this.packeryLayoutTimer);
            this.packeryLayoutTimer = setTimeout(() => {
                this.state.packeryInstance.layout();
            }, 50);
        }
    };

    onBlockHide = (el) => {
        if (this.state.packeryInstance) {
            this.state.packeryInstance.ignore(el);
            this.packeryLayout();
        }
    };

    onBlockShow = (el) => {
        if (this.state.packeryInstance) {
            this.state.packeryInstance.unignore(el);
            this.packeryLayout();
        }
    };

    onBlockDrag = (draggedItem) => {
        // See PackeryMods.js in this sourcecode for an explanation of how this
        // custom flag is set.
        if (!draggedItem.sbDragWasCancelled) {
            const {gridStore} = this.context;
            const newSortList = [];
            this.state.packeryInstance.getItemElements().forEach((el) => {
                newSortList.push(el.dataset.id);
            });
            gridStore.sortBlocksManually(newSortList);

            sb_updateGrid(gridStore);

            // get new coordinates for posx and posy so we can set them on the block and block will know position
            // has changed. Crucial for FB social block re-render.
            const newCoords = getBlockPosition(this.state.packeryInstance, draggedItem.element);
            this.context.gridStore.relocateBlock(draggedItem.element.dataset.id, newCoords.posX, newCoords.posY);
        }
    };

    reloadItems = () => {
        if (this.state.packeryInstance) {
            // When we reload items, everything gets set back to the original state (no ignored blocks), so
            // we need to make sure we reload all the stamps and call layout.
            clearTimeout(this.packeryReloadTimer);
            this.packeryReloadTimer = setTimeout(() => {
                // tell Packery to see if it needs to resize because we're not binding to that event
                this.state.packeryInstance.resize();
                this.state.packeryInstance.reloadItems();
                this.state.packeryInstance.stamps = [];
                this.state.packeryInstance.stamp('.sb-position-locked');
                this.packeryLayout();
            }, 50);
        }
    };

    toggleMap = () => {
        this.context.gridStore.toggleMapVisibility();
        this.packeryLayout();
    };

    handleFinalAsyncBlockLoaded = () => {
        const totalAsyncBlocks = this.props.blocks.filter(b => b.loadAsync).length
        const googCode = this.context.i18nStore.googleCode;
        return function() {
            totalAsyncBlocksLoaded++;
            console.debug(`${totalAsyncBlocksLoaded} of ${totalAsyncBlocks} async blocks loaded'`);
            if (totalAsyncBlocksLoaded === totalAsyncBlocks) {
                renderGoogleLanguage(googCode);
            }
        }
    }

    render() {
        const {htmlId, rowHeightClass, gridType} = this.props;
        const {gridStore, interfaceStore, organizationStore} = this.context;
        const grid = organizationStore.currentOrganization;

        const isSearchingGoogleDrive = organizationStore.organization.json_data?.settings?.identity?.google?.searchDrive;
        const isSearchingOffice365Drive = organizationStore.organization.json_data?.settings?.identity?.office365?.searchDrive;

        const gridClassName = classNames({
            'packery': true,
            [styles.packery]: true,
        });

        const displayFilterByCategory = this.props.displayFilterByCategory;
        const displayFilterByBlockType = this.props.displayFilterByBlockType;
        const displayFilterByText = this.props.displayFilterByText;
        const displayFilters = displayFilterByCategory || displayFilterByBlockType || displayFilterByText;
        const noResultsMsgClassName = classNames({
            'sb-content-width': true,
            'sb-no-filter-results-msg': true,
            'active': !gridStore.hasVisibleBlocks && displayFilters,
        });

        const rowHeightClassName = classNames({
            [rowHeightClass]: true,
            'sb-row-height-teacher-with-header': this.props.isPersonGrid,
        });

        let locksAreEnabled;
        switch (interfaceStore.breakpoint) {
            case 'break-point-xs':
                this.setMaxColumns(1);
                locksAreEnabled = false;
                break;

            case 'break-point-sm':
                this.setMaxColumns(2);
                locksAreEnabled = false;
                break;

            case 'break-point-md':
                this.setMaxColumns(this.props.hasMicrosite ? 2 : 3);
                locksAreEnabled = false;
                break;

            default:
                this.setMaxColumns(this.props.hasMicrosite ? 3 : 4);
                locksAreEnabled = true;
        }

        const blockObjNames = {};

        return (<ErrorBoundary>
            <ElementSpacer />
            {displayFilters && <Filters gridStore={gridStore}
                                        isSearchingGoogleDrive={isSearchingGoogleDrive}
                                        isSearchingOffice365Drive={isSearchingOffice365Drive}
                                        categoryFilters={displayFilterByCategory}
                                        blockTypeFilters={displayFilterByBlockType}
                                        textFilter={displayFilterByText}
                                        textFilterPlaceholder={this.props.textFilterPlaceholder}
                                        toggleMap={this.toggleMap}/>}
            <InteractiveCollectionMap enabled={this.props.displayMap}/>
            <main ref={this.gridRef} id={htmlId} className={gridClassName} role="main"
                  style={{paddingTop: gridType === gridTypesEnum.ALPHA_GRID ? "4rem" : 0}}
                  data-id={grid.id} data-grid-id={grid.id}>
                <div className="sb-column-width"/>
                <div className={rowHeightClassName}/>
                {
                    values(gridStore.blocks)
                        .filter(blockObj => {
                            if (this.props.hasMicrosite) {
                                return blockObj.blockType !== "section";
                            }
                            return true;
                        })
                        .map((blockObj) => {
                        // blockObj.sizeXY reflects the organization's preference for the block size, but sizeXY
                        // props reflect the rendered size
                        let sizeY = blockObj.sizeY, sizeX = blockObj.sizeX;
                        if (
                            sizeX === null ||
                            sizeX < 1 ||
                            interfaceStore.breakpoint === "break-point-xs"
                        ) {
                            sizeX = 1;
                        }
                        if (
                            !sizeY ||
                            sizeY < 1 ||
                            interfaceStore.breakpoint === "break-point-xs"
                        ) {
                            // if on small screen, block is bigger than 2, AND it's not a banner/button, display height of 2
                            // canUseDOM is here because when rehydrating the DOM, sizeY won't update appropriately on the client. breakpoint is always xs on the server
                            let json_data = blockObj.blockModel.json_data;
                            if (typeof json_data === 'string' && isJson(json_data)) json_data = JSON.parse(json_data);

                            if (blockObj.blockType === "message" && json_data.block.settings.items?.[0]?.image) {
                                // handle banners/buttons
                                sizeY = 1;
                            } else if (sizeY > 2) {
                                sizeY = 2;
                            }
                        }


                        // if name is not in blockObjNames and not hidden, it's first
                        let isFirst = false;

                        // This is tricky as we internationalize things.  If the first character is a digit, we want it
                        // to fall under our header for '#'.  Other characters (emoji, non-latin alphabet, etc) just just
                        // use the first character and move on.  Except diacritics should be grouped into the same heading
                        // as their regular latin equivalent.  So A and Á both fall under the heading A.
                        const firstCharacter = (blockObj.blockType === 'person' ? blockObj.blockModel.lname?.[0] : blockObj.filterString?.[0])?.toLocaleUpperCase();
                        const firstCharacterIsLetter = firstCharacter ? /[\D]/.test(firstCharacter) : null;
                        const normalizedFirstCharacter = firstCharacterIsLetter ? normalizeStringWithoutDiacritics(firstCharacter).toLocaleUpperCase() : "#";
                        if (!blockObjNames[normalizedFirstCharacter]) {
                            isFirst = true;
                            blockObjNames[normalizedFirstCharacter] = true;
                        }

                        return <Block key={blockObj.id}
                                      blank={false}
                                      blockObj={blockObj}
                                      isFirst={isFirst}
                                      gridType={gridType}
                                      locksAreEnabled={locksAreEnabled}
                                      outsideGrid={false}
                                      maxColumns={this.getMaxColumns()}
                                      normalizedFirstCharacter={normalizedFirstCharacter}
                                      packeryInstance={this.state.packeryInstance}
                                      packeryLayout={this.packeryLayout}
                                      sizeX={sizeX}
                                      sizeY={sizeY}
                                      tickLoadedAsync={this.tickTotalAsyncBlocksLoaded}
                                      isBlockPreview={false}
                                      hasMicrosite={this.props.hasMicrosite}
                        />
                    })
                }
            </main>
            <div className={noResultsMsgClassName}>
                <div className="sb-content-width">
                    <div className="sb-headerSchool-block clearfix">
                        <h3>Sorry, but there was nothing found. Please try different filters.</h3>
                    </div>
                </div>
            </div>
        </ErrorBoundary>);
    }
}

export default PackeryGrid
